import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
    createUserAsync,
    googleCreateAsync,
    selectUserResponse,
    loginUserAsync, // Auto-login after signup
} from "../authSlices";
import bg from "../image/image2.png";
import googlelogo from "../image/googlelogo.png";
import { useToast } from "@chakra-ui/react";
import "./Login.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { signInWithGoogle } from "../auth";
import { auth } from "../../../firebaseConfig";

// Employer location slices
import {
    fetchCitiesAsync,
    fetchCountryAsync,
    fetchStateAsync,
    selectFetchCities,
    selectFetchCountry,
    selectFetchState,
} from "../../employerDashboard/AboutYou/AboutSlice";

// ScreenLoader component with blue animation
const ScreenLoader = () => {
    return (
        <div className="loader-container">
            <div className="loader"></div>
        </div>
    );
};

export default function HomeSignup() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useToast();

    const userResponse = useSelector(selectUserResponse);

    // Country/State/City lists
    const countries = useSelector(selectFetchCountry);
    const states = useSelector(selectFetchState);
    const cities = useSelector(selectFetchCities);

    const [isLoading, setIsLoading] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedState, setSelectedState] = useState("");

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleConfirmPasswordVisibility = () =>
        setShowConfirmPassword(!showConfirmPassword);

    useEffect(() => {
        // Preload the background image
        const img = new Image();
        img.src = bg;
        img.onload = () => {
            setIsMounted(true);
        };
        // Fetch countries on mount
        dispatch(fetchCountryAsync());
    }, [dispatch]);

    // Fetch states when country changes
    useEffect(() => {
        if (selectedCountry) {
            dispatch(fetchStateAsync(selectedCountry));
            // Reset state and city when country changes
            setSelectedState("");
            setValue("state_uuid", "");
            setValue("city_uuid", "");
        }
    }, [selectedCountry, dispatch, setValue]);

    // Fetch cities when state changes
    useEffect(() => {
        if (selectedState) {
            dispatch(fetchCitiesAsync(selectedState));
            // Reset city when state changes
            setValue("city_uuid", "");
        }
    }, [selectedState, dispatch, setValue]);

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            // Create employer
            await dispatch(
                createUserAsync({
                    name: data.name,
                    email: data.email,
                    password: data.password,
                    phone: data.phone,
                    c_password: data.c_password,
                    user_type: "employer",
                    // Backend expects *_id, passing uuids as in your working signup
                    country_id: data.country_uuid,
                    state_id: data.state_uuid,
                    city_id: data.city_uuid,
                })
            ).unwrap();

            // Auto-login (non-blocking for navigation)
            try {
                await dispatch(
                    loginUserAsync({ email: data.email, password: data.password })
                ).unwrap();
            } catch (loginErr) {
                console.warn("Auto-login failed after employer signup:", loginErr);
            }

            toast({
                title: "Signup successful",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });

            // Redirect to phone verification with redirectTo for employers
            navigate("/phone-verification", {
                replace: true,
                state: {
                    phone: data.phone,
                    email: data.email,
                    redirectTo: "/employer-basics",
                },
            });
        } catch (e) {
            toast({
                title: "Error in Signup",
                description: e?.message || "An error occurred during signup.",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        try {
            const { accessToken } = await signInWithGoogle();
            const payload = {
                accessToken,
                user_type: "employer",
            };

            const response = await dispatch(googleCreateAsync(payload)).unwrap();
            const loginType = response?.login_type;
            const userType = response?.user?.user_type;
            const phone = response?.user?.phone || "";

            if (userType !== "employer") {
                toast({
                    title: "Login Unsuccessful",
                    description: "This is employer login only.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                try {
                    await auth.signOut();
                } catch { }
                return;
            }

            if (loginType === "login") {
                navigate("/employer-dashboard");
                toast({
                    title: "Google login successful.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
            } else if (loginType === "register") {
                navigate("/phone-verification", {
                    state: { phone, redirectTo: "/employer-basics" },
                });
            }
        } catch (error) {
            toast({
                title: "Google login failed.",
                description: error?.message || "There was an error logging in with Google.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
            console.error("Google login error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isMounted) {
        return <ScreenLoader />;
    }

    return (
        <div className="bg-white">
            {isLoading && <ScreenLoader />}

            <div className="w-full p-4 sm:p-6 overflow-y-auto">
                <div className="text-left font-semibold text-xl sm:text-2xl text-textBlue mb-1">
                    IndiaHirings.com
                </div>

                <p className="text-left text-gray-600 mb-2 font-medium">
                    Create your Employer account to get started
                </p>

                <form
                    className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3"
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                            placeholder="Enter your name"
                            {...register("name", { required: "Name is required" })}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-sm">{errors.name.message}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                            placeholder="Enter your email"
                            {...register("email", {
                                required: "Email is required",
                                pattern: {
                                    value: /\b[\w.-]+@[\w.-]+\.\w{2,4}\b/gi,
                                    message: "Email is not valid",
                                },
                            })}
                        />
                        {errors.email && (
                            <p className="text-red-500 text-sm">{errors.email.message}</p>
                        )}
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                                placeholder="Enter your password"
                                {...register("password", {
                                    required: "Password is required",
                                    pattern: {
                                        value:
                                            /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
                                        message:
                                            "Password must have at least 6 characters, 1 uppercase letter, 1 number, and 1 special character.",
                                    },
                                })}
                            />
                            <span
                                className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                                onClick={togglePasswordVisibility}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                        {errors.password && (
                            <p className="text-red-500 text-sm">
                                {errors.password.message}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                                placeholder="Confirm your password"
                                {...register("c_password", {
                                    required: "Confirm Password is required",
                                    validate: (value) =>
                                        value === watch("password") || "Passwords do not match",
                                })}
                            />
                            <span
                                className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                                onClick={toggleConfirmPasswordVisibility}
                            >
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                        {errors.c_password && (
                            <p className="text-red-500 text-sm">
                                {errors.c_password.message}
                            </p>
                        )}
                    </div>

                    {/* Phone */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            maxLength={10}
                            className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                            placeholder="Enter your phone number"
                            {...register("phone", {
                                required: "Phone number is required",
                                pattern: {
                                    value: /^\d{10}$/,
                                    message: "Phone number must be 10 digits",
                                },
                            })}
                        />
                        {errors.phone && (
                            <p className="text-red-500 text-sm">{errors.phone.message}</p>
                        )}
                    </div>

                    {/* Country */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            Country <span className="text-red-500">*</span>
                        </label>
                        <select
                            className={`w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600`}
                            {...register("country_uuid", { required: "Country is required" })}
                            onChange={(e) => {
                                setSelectedCountry(e.target.value);
                                setValue("country_uuid", e.target.value);
                            }}
                            value={selectedCountry}
                        >
                            <option value="">Select Country</option>
                            {countries?.map((country) => (
                                <option key={country.uuid} value={country.uuid}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                        {errors.country_uuid && (
                            <p className="text-red-500 text-sm">
                                {errors.country_uuid.message}
                            </p>
                        )}
                    </div>

                    {/* State */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            State <span className="text-red-500">*</span>
                        </label>
                        <select
                            className={`w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600`}
                            {...register("state_uuid", { required: "State is required" })}
                            onChange={(e) => {
                                setSelectedState(e.target.value);
                                setValue("state_uuid", e.target.value);
                            }}
                            value={selectedState}
                            disabled={!selectedCountry}
                        >
                            <option value="">Select State</option>
                            {states?.map((state) => (
                                <option key={state.uuid} value={state.uuid}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        {errors.state_uuid && (
                            <p className="text-red-500 text-sm">
                                {errors.state_uuid.message}
                            </p>
                        )}
                    </div>

                    {/* City */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-600">
                            City <span className="text-red-500">*</span>
                        </label>
                        <select
                            className={`w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600`}
                            {...register("city_uuid", { required: "City is required" })}
                            disabled={!selectedState}
                        >
                            <option value="">Select City</option>
                            {cities?.map((city) => (
                                <option key={city.uuid} value={city.uuid}>
                                    {city.name}
                                </option>
                            ))}
                        </select>
                        {errors.city_uuid && (
                            <p className="text-red-500 text-sm">
                                {errors.city_uuid.message}
                            </p>
                        )}
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        className="w-full col-span-2 px-6 py-3 mt-2 text-white font-semibold uppercase bg-fontBlue hover:bg-textBlue focus:outline-none rounded-lg"
                        disabled={isLoading}
                    >
                        {isLoading ? "Signing up..." : "Sign Up"}
                    </button>
                </form>

                <div className="relative mt-2 sm:col-span-2">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">OR</span>
                    </div>
                </div>

                <div className="mt-2 sm:col-span-2">
                    <button
                        onClick={handleGoogleLogin}
                        className="flex items-center justify-center w-full px-6 py-3 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 focus:outline-none"
                        disabled={isLoading}
                    >
                        <img
                            src={googlelogo || "/placeholder.svg"}
                            alt="Google Logo"
                            className="h-6 w-6 object-cover"
                        />
                        <span className="ml-2">
                            {isLoading ? "Signing up..." : "Sign Up with Google"}
                        </span>
                    </button>
                </div>

                <p className="mt-1.5 text-sm text-center text-gray-600 sm:col-span-2">
                    Already have an account?{" "}
                    <Link className="font-bold text-textBlue" to="/login">
                        Log in
                    </Link>
                </p>
            </div>
        </div>
    );
}